import { defineStore as define } from 'pinia'
import { ref, computed } from 'vue'
import {
  query,
  limit,
  order_by,
  list,
  participants as participants_ref,
  doc,
  load,
  where,
  ref_doc,
  set_doc
} from '@/utils/serverless'
import { hydrate_list } from '@/utils/common'
import type { Participant, User } from '@/types'
import { savable_date } from '@/utils/serverless.js'
const get_status_constraints = (status?: string) => {
  if (!status) return []

  const lower_status = status.toLowerCase()

  if (lower_status === 'rejected') {
    return [where('status', '==', 'rejected')]
  }

  if (lower_status === 'approved') {
    return [where('status', '==', 'approved')]
  }

  if (['cleared', 'paid', 'written', 'draft'].includes(lower_status)) {
    return [
      where('status', '==', 'approved'),
      where('story.status', '==', lower_status)
    ]
  }

  return [where('status', '==', lower_status)]
}

export const by_project = (project_id: string, status?: string) => {
  const base_query = participants_ref(project_id)
  const constraints = [
    ...get_status_constraints(status),
    order_by('created', 'desc'),
    limit(21)
  ]
  return query(base_query, ...constraints)
}

export const use_participants = define('participants', () => {
  const participants_list = ref<Participant[]>([])
  const last_created = ref(null)
  const loading = ref(false)
  const cached_project_id = ref(null)

  const enriched_participants = computed(() => {
    if (!participants_list.value?.length) return []

    return participants_list.value.map(participant => ({
      ...participant,
      story: participant.story || null,
      status: participant.status?.toLowerCase()
    }))
  })

  const page_size = 21

  const enrich_participants = async basic_participants => {
    if (!basic_participants?.length) return []

    const user_promises = basic_participants.map(participant =>
      load(doc(`users/${participant.ref.id}`))
    )

    const user_snapshots = await Promise.all(user_promises)
    const user_data = user_snapshots.map(snap => snap.data())

    return basic_participants.map((participant, index) => ({
      id: user_data[index]?.id,
      name: user_data[index]?.name,
      username: user_data[index]?.username,
      email: user_data[index]?.email,
      phone: user_data[index]?.phone,
      city: user_data[index]?.city,
      avatar: user_data[index]?.avatar,
      integrity: user_data[index]?.integrity,
      ...participant,
      user: user_data[index]
    }))
  }

  const load_participants = async (project_id: string, status?: string) => {
    loading.value = true
    participants_list.value = []
    last_created.value = null
    cached_project_id.value = project_id

    try {
      const snapshot = await list(by_project(project_id, status))

      if (!snapshot.empty) {
        const basic_participants = hydrate_list(snapshot)
        participants_list.value = await enrich_participants(basic_participants)
        cached_project_id.value = project_id

        if (participants_list.value.length) {
          last_created.value =
            participants_list.value[participants_list.value.length - 1].created
        }
      }
    } catch (error) {
      console.error('Participants store: Error loading participants:', error)
      participants_list.value = []
      cached_project_id.value = null
    }

    loading.value = false
    return participants_list.value
  }

  const load_more_participants = async (
    project_id: string,
    status?: string
  ) => {
    if (loading.value || !last_created.value) return false

    console.time('Loading more participants')
    const constraints = [
      ...get_status_constraints(status),
      where('created', '<', last_created.value),
      order_by('created', 'desc'),
      limit(page_size)
    ]

    const next_query = query(participants_ref(project_id), ...constraints)
    const snapshot = await list(next_query)
    console.timeEnd('Loading more participants')

    if (!snapshot.empty) {
      const basic_participants = hydrate_list(snapshot)
      const enriched_participants =
        await enrich_participants(basic_participants)
      participants_list.value = [
        ...participants_list.value,
        ...enriched_participants
      ]

      if (enriched_participants.length) {
        last_created.value =
          enriched_participants[enriched_participants.length - 1].created
        return true
      }
    }

    last_created.value = null
    return false
  }

  const clear_cache = () => {
    participants_list.value = []
    cached_project_id.value = null
    last_created.value = null
  }

  const add_participant_to_project = async (project_id, user_id) => {
    console.log('add_participant_to_project', project_id, user_id)
    const snapshot = await load(doc(`users/${user_id}`))
    console.log('snapshot', snapshot)
    const parp = snapshot.data() as User
    console.log('parp', parp)
    await set_doc(doc(`projects/${project_id}/participants/${user_id}`), {
      created: savable_date(),
      updated: savable_date(),
      address: parp.address,
      avatar: parp.avatar,
      demographics: parp.demographics,
      status: 'approved',
      ref: doc(`users/${user_id}`),
      username: parp.username
    })
  }

  const load_participant = async (user_id: string, project_id: string) => {
    const snapshot = await load(
      doc(`projects/${project_id}/participants/${user_id}`)
    )
    return snapshot.data() as Participant
  }

  const load_all_filtered_participants = async (
    project_id: string,
    status: string
  ) => {
    loading.value = true
    participants_list.value = []
    cached_project_id.value = project_id

    try {
      const base_query = participants_ref(project_id)
      const constraints = [
        ...get_status_constraints(status),
        order_by('created')
      ]
      const snapshot = await list(query(base_query, ...constraints))

      if (!snapshot.empty) {
        const basic_participants = hydrate_list(snapshot)
        participants_list.value = await enrich_participants(basic_participants)
        cached_project_id.value = project_id
      }
    } catch (error) {
      console.error(
        'Participants store: Error loading all filtered participants:',
        error
      )
      participants_list.value = []
      cached_project_id.value = null
    }

    loading.value = false
    return participants_list.value
  }

  return {
    participants: enriched_participants,
    raw_participants: participants_list,
    last_created,
    loading,
    load_participants,
    load_participant,
    load_more_participants,
    clear_cache,
    add_participant_to_project,
    load_all_filtered_participants
  }
})
